import React, { useState } from "react";
import download from "downloadjs";
import { toast } from "react-toastify";
import { AppStrings } from "../../../utils/strings";
import { styles } from "./styles";
import { Axios } from "../../../../../services/axios";
import { useAppSelector } from "../../../../../app/hooks";
import hazard from "../../../../../assets/svg/Hazard.svg";
import check from "../../../../../assets/svg/Check.svg";
import { QuoteClose, QuoteDownload, QuoteMail, QuoteMobile } from "../../../images/svg";

export const QuoteManagerModal = ({ onClose }: { onClose: () => void }) => {
  const authState = useAppSelector((state) => state.auth);
  const { calculator } = useAppSelector((state) => state.remittance);

  const [activeTab, setActiveTab] = useState<"default" | "downloaded" | "email" | "sms">("default");

  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  const [emailVal, setEmailVal] = useState("");
  const [emailInputs, setEmailInputs] = useState<string[]>([]);
  const [emailErrors, setEmailErrors] = useState("");

  const [mobileNumbers, setMobileNumbers] = useState<string[]>([""]);
  const [mobileErrors, setMobileErrors] = useState<string[]>([""]);
  const [disableAddMob, setDisableAddMob] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* --------------------------------------------------------------
      UTIL: Extract common API payload from transactionData
  -------------------------------------------------------------- */
  const buildCommonPayload = (vendorCode: any) => {
    const {
      conversion_amount,
      reciepient_recieve,
      conversion_rate,
      dest_currency,
      total_tax,
      nostro_charges,
      nostro_charges_new,
      hop_fees,
      tcs,
      transaction_purpose,
    } = calculator?.getTransactionSummary?.data;

    const totalCharges = total_tax + nostro_charges + nostro_charges_new + hop_fees + tcs;

    const youPay = Number(conversion_amount) + Number(totalCharges);

    return {
      transVendorKey: vendorCode.userId,
      destinationCurrency: dest_currency,
      conversionRate: Number(conversion_rate),
      recipientReceive: Number(reciepient_recieve),
      taxes: total_tax,
      totalPay: youPay,
      purposeCode: transaction_purpose,
      userSend: Number(conversion_amount),
      tcs,
      bankFee: nostro_charges,
      nostro: nostro_charges_new,
      vendorFee: hop_fees,
    };
  };

  /* --------------------------------------------------------------
      1. DOWNLOAD QUOTE (Merged + optimized function)
  -------------------------------------------------------------- */
  const handleDownloadQuoteClick = async () => {
    const vendorCode = calculator?.amountCalculation?.vendorDropdown;
    const userId = authState?.userKey;
    const currentUser = authState?.currentUser;

    if (!vendorCode?.userId || !calculator?.amountCalculation?.transaction_purpose) {
      toast.error(AppStrings.QUOTE_MANAGER.FILL_VENDOR_PURPOSE);
      return;
    }

    const payload = buildCommonPayload(vendorCode);

    try {
      const response = await Axios.get(`remit/user/${userId}/quote`, {
        params: payload,
        headers: { Authorization: currentUser.idToken },
        responseType: "blob",
      });

      if (response.status === 200) {
        const content = response.headers["content-type"];
        setMessage(AppStrings.QUOTE_MANAGER.FILE_READY);

        setTimeout(() => {
          download(response.data, "User Quote.pdf", content);
          setMessage("");
        }, 2000);
      }
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.error?.type || AppStrings.QUOTE_MANAGER.DOWNLOAD_FAILED;

      toast.error(errorMessage);
    }
  };

  /* --------------------------------------------------------------
      2. SEND QUOTE (Email / SMS)
  -------------------------------------------------------------- */
  const handleSendQuote = async () => {
    const vendorCode = calculator?.amountCalculation?.vendorDropdown;
    const userId = authState?.userKey;
    const currentUser = authState?.currentUser;

    if (!vendorCode?.userId || !calculator?.amountCalculation?.transaction_purpose) {
      toast.error(AppStrings.QUOTE_MANAGER.FILL_VENDOR_PURPOSE);
      return;
    }

    const basePayload: any = {
      ...buildCommonPayload(vendorCode),
      sendType: activeTab,
    };

    if (activeTab === "sms") {
      basePayload.mobiles = mobileNumbers.filter((m) => /^\d{10}$/.test(m));
    } else if (activeTab === "email") {
      basePayload.emailId = emailInputs;
    }

    try {
      const res = await Axios.post(`/remit/user/${userId}/sendQuotesLink`, basePayload, {
        headers: { Authorization: currentUser.idToken },
      });

      if (res.data?.success) {
        const successMsg =
          activeTab === "sms"
            ? AppStrings.QUOTE_MANAGER.SMS_SUCCESS
            : AppStrings.QUOTE_MANAGER.EMAIL_SUCCESS;

        setMessage(successMsg);
        setMobileNumbers([""]);
        setEmailInputs([]);
        setMobileErrors([""]);
        setEmailErrors("");
      } else {
        toast.error(res.data?.error?.type || AppStrings.GENERIC_ERROR);
      }
    } catch (err: any) {
      setError(true);
      const failMsg =
        activeTab === "sms"
          ? AppStrings.QUOTE_MANAGER.SMS_FAILED
          : AppStrings.QUOTE_MANAGER.EMAIL_FAILED;

      setMessage(failMsg);
    }

    setTimeout(() => setMessage(""), 2500);
  };

  /* --------------------------------------------------------------
      TAB HANDLERS
  -------------------------------------------------------------- */
  const handleDownload = () => {
    setActiveTab("downloaded");
    handleDownloadQuoteClick();
  };

  /* --------------------------------------------------------------
      RENDER
  -------------------------------------------------------------- */
  return (
    <div className={styles.modal.overlay}>
      <div className={styles.modal.container}>
        {/* Header */}
        <div className={styles.modal.header}>
          <h2 className={styles.modal.title}>{AppStrings.QUOTE_MANAGER.TITLE}</h2>

          <button onClick={onClose} className={styles.modal.closeBtn}>
            <QuoteClose />
          </button>
        </div>

        <div className={styles.modal.body}>
          <p className={styles.modal.sectionTitle}>{AppStrings.QUOTE_MANAGER.SHARE_QUOTE}</p>

          {/* Tabs */}
          <div className={styles.tabs.wrapper}>
            {/* Download Tab */}
            <button
              onClick={handleDownload}
              className={`${styles.tabs.base} ${
                activeTab === "downloaded" ? styles.tabs.active : styles.tabs.inactive
              }`}
            >
              <QuoteDownload />
              {AppStrings.QUOTE_MANAGER.DOWNLOAD}
            </button>

            {/* Mobile Tab */}
            <button
              onClick={() => setActiveTab("sms")}
              className={`${styles.tabs.base} ${
                activeTab === "sms" ? styles.tabs.active : styles.tabs.inactive
              }`}
            >
              <QuoteMobile />
              {AppStrings.QUOTE_MANAGER.MOBILE}
            </button>

            {/* Email Tab */}
            <button
              onClick={() => setActiveTab("email")}
              className={`${styles.tabs.base} ${
                activeTab === "email" ? styles.tabs.active : styles.tabs.inactive
              }`}
            >
              <QuoteMail />
              {AppStrings.QUOTE_MANAGER.EMAIL}
            </button>
          </div>

          {/* EMAIL SECTION */}
          {activeTab === "email" && (
            <>
              <div className="flex flex-col gap-y-4 mt-2 w-full">
                <div className="flex flex-col gap-1">
                  {/* Email list */}
                  {emailInputs.map((email, index) => (
                    <div className={styles.email.input} key={index}>
                      {email}
                      <button
                        className={styles.mobile.removeBtn}
                        onClick={() => setEmailInputs(emailInputs.filter((_, i) => i !== index))}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  {/* Input + Add */}
                  {emailInputs.length < 5 && (
                    <>
                      <div className="flex gap-3 w-full items-center">
                        <input
                          type="email"
                          placeholder={AppStrings.QUOTE_MANAGER.PLACEHOLDER_EMAIL}
                          className={styles.email.input}
                          value={emailVal}
                          onChange={(e) => setEmailVal(e.target.value)}
                        />

                        <button
                          disabled={!emailRegex.test(emailVal)}
                          onClick={() => {
                            setEmailInputs([...emailInputs, emailVal]);
                            setEmailVal("");
                          }}
                          className={styles.email.addButton}
                        >
                          + Add
                        </button>
                      </div>

                      <p className={styles.email.note}>
                        Please click the Add button to save the email before clicking the Send
                        button.
                      </p>

                      {emailErrors !== "" && <p className={styles.email.error}>{emailErrors}</p>}
                    </>
                  )}
                </div>
              </div>

              <button
                disabled={emailInputs.length === 0 || emailErrors !== ""}
                onClick={handleSendQuote}
                className={styles.buttons.send}
              >
                {AppStrings.QUOTE_MANAGER.SEND}
              </button>
            </>
          )}

          {/* MOBILE SECTION */}
          {activeTab === "sms" && (
            <>
              <div className="flex flex-col gap-y-4 mt-2 w-full">
                {mobileNumbers.map((num, index) => (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="flex gap-3 w-full items-center">
                      <input
                        type="text"
                        placeholder={AppStrings.QUOTE_MANAGER.PLACEHOLDER_MOBILE}
                        value={mobileNumbers[index]}
                        inputMode="numeric"
                        className={styles.mobile.input}
                        onChange={(e) => {
                          let val = e.target.value.replace(/\D/g, "").slice(0, 10);

                          const updated = [...mobileNumbers];
                          updated[index] = val;
                          setMobileNumbers(updated);

                          const errList = [...mobileErrors];
                          errList[index] =
                            val.length === 10 ? "" : AppStrings.QUOTE_MANAGER.MOBILE_INVALID;
                          setMobileErrors(errList);

                          setDisableAddMob(val.length !== 10);
                        }}
                      />

                      {mobileNumbers.length > 1 && (
                        <button
                          onClick={() =>
                            setMobileNumbers(mobileNumbers.filter((_, i) => i !== index))
                          }
                          className={styles.mobile.removeBtn}
                        >
                          ✕
                        </button>
                      )}

                      {index === mobileNumbers.length - 1 && mobileNumbers.length < 5 && (
                        <button
                          disabled={mobileErrors[index] !== "" || disableAddMob}
                          onClick={() => setMobileNumbers([...mobileNumbers, ""])}
                          className={styles.mobile.addBtn}
                        >
                          + Add
                        </button>
                      )}
                    </div>

                    {mobileErrors[index] ? (
                      <p className={styles.mobile.error}>{mobileErrors[index]}</p>
                    ) : (
                      <p className={styles.mobile.hint}>{AppStrings.QUOTE_MANAGER.MOBILE_HINT}</p>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleSendQuote}
                disabled={
                  mobileNumbers.every((m) => m.trim() === "") ||
                  mobileNumbers.some((m) => m.length !== 10) ||
                  mobileErrors.some((err) => err !== "")
                }
                className={styles.buttons.send}
              >
                {AppStrings.QUOTE_MANAGER.SEND}
              </button>
            </>
          )}

          {/* Confirmation Message */}
          {message && (
            <div
              className={`${styles.message.base} ${
                error ? styles.message.error : styles.message.success
              }`}
            >
              <img src={error ? hazard : check} className="pr-2" />
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
